// Elapsed-time progress spinner for executive-summary generation. Fills an
// SVG arc from 0% to 100% over `estimatedMs` (default 55s). Past 100% the
// spinner keeps ticking with a slow asymptotic approach toward 99% so the
// user doesn't see it sit at 100% while the server is still working.
//
// When the parent signals completion via `completing=true`, the spinner
// runs a short tween from its current pct up to 100 and calls
// `onCompletionFinished`, giving the parent a handshake to swap in the
// rendered payload without a jarring jump.
//
// Pure UI — no backend progress stream. The 55s baseline is an educated
// guess; when real progress becomes available, swap in a `progressPct`
// prop and short-circuit the timer.

import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

interface Props {
  /** Estimated completion in ms. Defaults to 55000. */
  estimatedMs?: number;
  /** Diameter of the spinner in px. Defaults to 32. */
  size?: number;
  /** Stroke color for the filled arc. Defaults to the primary blue. */
  color?: string;
  /**
   * When the parent knows generation is actually done, set this to true.
   * The spinner stops its elapsed-time timer and tweens the arc from the
   * current pct to 100 over ~400ms, then fires `onCompletionFinished`.
   */
  completing?: boolean;
  /** Fired once when the completion tween lands on 100. */
  onCompletionFinished?: () => void;
}

const DEFAULT_ESTIMATED_MS = 55_000;
const TICK_MS = 100;
const STROKE_WIDTH = 3;
const COMPLETION_TWEEN_MS = 400;

export default function ExecutiveSummaryProgress({
  estimatedMs = DEFAULT_ESTIMATED_MS,
  size = 32,
  color = '#1271c4',
  completing = false,
  onCompletionFinished,
}: Props): React.JSX.Element {
  const startRef = useRef<number>(Date.now());
  const [elapsedPct, setElapsedPct] = useState(0);
  const [completionPct, setCompletionPct] = useState<number | null>(null);

  // Elapsed-time ticker. Stops as soon as `completing` flips so the arc
  // freezes before the tween takes over.
  useEffect(() => {
    if (completing) return;
    startRef.current = Date.now();
    setElapsedPct(computeDisplayPct(0, estimatedMs));
    const id = window.setInterval(() => {
      setElapsedPct(computeDisplayPct(Date.now() - startRef.current, estimatedMs));
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [completing, estimatedMs]);

  // Completion tween. Linearly animates from the last elapsed pct up to
  // 100 and fires the handshake once.
  useEffect(() => {
    if (!completing) return;
    const from = elapsedPct;
    const tweenStart = performance.now();
    let rafId = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - tweenStart) / COMPLETION_TWEEN_MS);
      setCompletionPct(from + (100 - from) * easeOutCubic(t));
      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        onCompletionFinished?.();
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
    // elapsedPct is intentionally excluded — we capture its value at the
    // moment completion begins and ignore subsequent updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completing]);

  const pct = completing && completionPct != null ? completionPct : elapsedPct;
  const radius = (size - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct / 100);

  return (
    <SpinnerRoot
      $size={size}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(18, 113, 196, 0.15)"
          strokeWidth={STROKE_WIDTH}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: completing ? 'none' : `stroke-dashoffset ${TICK_MS}ms linear`,
          }}
        />
      </svg>
      <PctLabel $size={size}>{Math.round(pct)}%</PctLabel>
    </SpinnerRoot>
  );
}

// Linear progress from 0 → 90% over the estimated window, then asymptotic
// creep from 90 → 99% so the user never sees a stuck "100%" while the
// server is still generating. Final 1% is reserved for the actual
// completion handoff via the `completing` prop.
function computeDisplayPct(elapsedMs: number, estimatedMs: number): number {
  const linearCap = 90;
  const linearPct = Math.min(linearCap, (elapsedMs / estimatedMs) * linearCap);
  if (elapsedMs <= estimatedMs) return linearPct;
  const overtime = elapsedMs - estimatedMs;
  const creep = (1 - Math.exp(-overtime / (estimatedMs * 2))) * 9;
  return Math.min(99, linearCap + creep);
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

const SpinnerRoot = styled.div<{ $size: number }>`
  position: relative;
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PctLabel = styled.span<{ $size: number }>`
  position: absolute;
  font-size: ${(p) => Math.max(9, Math.round(p.$size * 0.3))}px;
  font-weight: 600;
  color: #1271c4;
  font-variant-numeric: tabular-nums;
  line-height: 1;
`;
